// Feedback controls under every answer.
// - "Good" fires immediately (no modal).
// - "Wrong" or "Incomplete" opens an in-place structured form:
//     * What was incorrect (freeform, required)
//     * What should the answer have been (optional)
//     * Category (optional dropdown)
// Submission calls /api/feedback with the enriched payload. The row lands
// in the moderation queue with status='pending' — admin reviews later.

import { useState } from 'react'
import { submitFeedback } from '../lib/api'
import type { FeedbackRating, FeedbackCategory } from '../lib/types'

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'incorrect_data', label: 'Incorrect data (wrong number/fact)' },
  { value: 'wrong_interpretation', label: 'Wrong interpretation of the question' },
  { value: 'missing_context', label: 'Missing important context' },
  { value: 'definition_ambiguity', label: 'Definition/term is ambiguous' },
  { value: 'tool_behavior', label: 'Tool behavior (search/query/etc.)' },
  { value: 'other', label: 'Other' },
]

export function FeedbackButtons({ auditId }: { auditId: string }) {
  const [submitted, setSubmitted] = useState<FeedbackRating | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [pendingRating, setPendingRating] = useState<FeedbackRating | null>(null)
  const [whatWasWrong, setWhatWasWrong] = useState('')
  const [suggestedAnswer, setSuggestedAnswer] = useState('')
  const [category, setCategory] = useState<'' | FeedbackCategory>('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleClick(rating: FeedbackRating) {
    if (rating === 'good') {
      setSending(true)
      try {
        await submitFeedback(auditId, 'good')
        setSubmitted('good')
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setSending(false)
      }
    } else {
      setPendingRating(rating)
      setShowForm(true)
    }
  }

  async function submitStructured() {
    if (!pendingRating) return
    setSending(true)
    setError(null)
    try {
      await submitFeedback(auditId, {
        rating: pendingRating,
        what_was_wrong: whatWasWrong.trim() || undefined,
        suggested_answer: suggestedAnswer.trim() || undefined,
        category: (category || undefined) as FeedbackCategory | undefined,
      })
      setSubmitted(pendingRating)
      setShowForm(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSending(false)
    }
  }

  if (submitted) {
    return (
      <div className="mt-2 text-xs text-marker-muted">
        Thanks — feedback saved
        {submitted !== 'good' && <span> (pending review — {submitted})</span>}
        {submitted === 'good' && <span> (good)</span>}
      </div>
    )
  }

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-marker-muted">Was this useful?</span>
        <button
          onClick={() => handleClick('good')}
          disabled={sending}
          className="px-2 py-1 rounded border border-marker-border hover:border-marker-good hover:text-marker-good disabled:opacity-40"
          title="Answer was correct and useful"
        >
          👍 Good
        </button>
        <button
          onClick={() => handleClick('incomplete')}
          disabled={sending}
          className="px-2 py-1 rounded border border-marker-border hover:border-marker-warn hover:text-marker-warn disabled:opacity-40"
          title="Answer was partial or missing something"
        >
          ⚠ Incomplete
        </button>
        <button
          onClick={() => handleClick('wrong')}
          disabled={sending}
          className="px-2 py-1 rounded border border-marker-border hover:border-marker-bad hover:text-marker-bad disabled:opacity-40"
          title="Answer was factually wrong"
        >
          👎 Wrong
        </button>
      </div>

      {showForm && (
        <div className="mt-3 p-3 border border-marker-border rounded-lg bg-marker-bg space-y-3">
          <div>
            <label className="block text-xs text-marker-muted mb-1">
              What was {pendingRating === 'wrong' ? 'wrong' : 'incomplete'}?
              <span className="text-marker-bad ml-1">*</span>
            </label>
            <textarea
              value={whatWasWrong}
              onChange={(e) => setWhatWasWrong(e.target.value)}
              placeholder="e.g., The MRR total was $8,400, not $6,200 as shown"
              rows={2}
              className="w-full text-sm bg-marker-panel border border-marker-border rounded p-2 text-marker-text placeholder:text-marker-muted focus:outline-none focus:border-marker-accent"
            />
          </div>

          <div>
            <label className="block text-xs text-marker-muted mb-1">
              What should the answer have been? <span className="text-marker-muted">(optional)</span>
            </label>
            <textarea
              value={suggestedAnswer}
              onChange={(e) => setSuggestedAnswer(e.target.value)}
              placeholder="Correct answer, or the correction that should have been made"
              rows={3}
              className="w-full text-sm bg-marker-panel border border-marker-border rounded p-2 text-marker-text placeholder:text-marker-muted focus:outline-none focus:border-marker-accent"
            />
          </div>

          <div>
            <label className="block text-xs text-marker-muted mb-1">
              Category <span className="text-marker-muted">(optional)</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory | '')}
              className="w-full text-sm bg-marker-panel border border-marker-border rounded p-2 text-marker-text focus:outline-none focus:border-marker-accent"
            >
              <option value="">Uncategorized</option>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {error && <div className="text-xs text-marker-bad">Error: {error}</div>}

          <div className="flex items-center justify-end gap-2 text-xs">
            <button
              onClick={() => {
                setShowForm(false)
                setPendingRating(null)
                setWhatWasWrong('')
                setSuggestedAnswer('')
                setCategory('')
                setError(null)
              }}
              className="px-3 py-1 text-marker-muted hover:text-marker-text"
            >
              Cancel
            </button>
            <button
              onClick={submitStructured}
              disabled={sending || !whatWasWrong.trim()}
              className="px-3 py-1 bg-marker-accent text-white rounded hover:bg-marker-accent2 disabled:opacity-40 disabled:cursor-not-allowed"
              title={!whatWasWrong.trim() ? 'Please describe what was wrong' : 'Submit for admin review'}
            >
              {sending ? 'Submitting…' : 'Submit for review'}
            </button>
          </div>
        </div>
      )}

      {error && !showForm && (
        <div className="mt-1 text-xs text-marker-bad">Error: {error}</div>
      )}
    </div>
  )
}
